import { plural, t, Trans } from "@lingui/macro";
import { Button, CircularProgress, Stack } from "@mui/material";
import { useAuthState } from "@saleor/sdk";
import { Formik, FormikErrors, FormikHelpers, FormikValues, useFormikContext } from "formik";
import React from "react";

import {
	AttributeFragment,
	AttributeInputTypeEnum,
	CheckoutDetailsFragment,
	CheckoutPreferenceValueFragment,
	SimpleProductFragment,
	useCreateCheckoutLinePreferenceMutation,
	User,
	UserDraftCheckoutsDocument,
	useUpdateCheckoutLinePreferenceMutation,
} from "@/saleor/api";

import { FormValues } from "../checkout/cart/schema";
import useChannelContext from "../shared/contexts/ChannelContext";
import PersonaliseField from "./PersonaliseField";
import {
	applyValueToCustomiseTemplate,
	getAttributesValidationMeta,
	normalizePreferenceValue,
} from "./utils";

export interface ProductPersonalizeProps {
	fieldPrefix?: string;
	active?: boolean;
	checkout?: CheckoutDetailsFragment;
	product?: SimpleProductFragment;
	variant?: "product" | "checkout";
}

const ProductPersonalize: React.VFC<ProductPersonalizeProps> = ({
	fieldPrefix,
	product,
	active = false,
	checkout,
	variant = "checkout",
}) => {
	const { channel } = useChannelContext();
	const [updateCheckoutLinePreference] = useUpdateCheckoutLinePreferenceMutation({
		refetchQueries: [
			{
				query: UserDraftCheckoutsDocument,
				variables: { first: 100, channel, multipleCheckout: false },
			},
		],
	});
	const [createCheckoutLinePreference] = useCreateCheckoutLinePreferenceMutation({
		refetchQueries: [
			{
				query: UserDraftCheckoutsDocument,
				variables: { first: 100, channel, multipleCheckout: false },
			},
		],
	});
	const { user } = useAuthState();
	const { setFieldValue } = useFormikContext<FormValues>();

	const savedPreferences = React.useMemo(() => {
		if (checkout?.lines && checkout.lines[0]) {
			const checkoutLine = checkout.lines[0];
			return (
				checkoutLine.preferences?.reduce<{
					[key: string]: CheckoutPreferenceValueFragment;
				}>((a, c) => {
					if (c?.attribute) {
						a[c.attribute.id] = c;
					}
					return a;
				}, {}) || {}
			);
		}
		return {};
	}, [checkout?.lines]);

	const attributes = React.useMemo(() => {
		if (product) {
			return product.attributes;
		}
		if (checkout?.lines && checkout.lines[0]) {
			return checkout.lines[0].variant.product.attributes;
		}
		return [];
	}, [checkout?.lines, product]);

	const initialValues = React.useMemo<FormikValues>(() => {
		return attributes.reduce<FormikValues>((a, c) => {
			if (c.attribute) {
				a[c.attribute.id] = savedPreferences[c.attribute.id]?.value;
			}
			return a;
		}, {});
	}, [attributes, savedPreferences]);

	if (attributes.length === 0) {
		return null;
	}

	if (variant === "product") {
		return (
			<div className="grid grid-cols-4 gap-2">
				<div className="col-span-2 md:col-span-1 text-sm font-semibold pt-2">
					<Trans>Personalise</Trans>
				</div>
				<div className="col-span-4 md:col-span-3 border border-gray-300 rounded-md px-4 pt-3 pb-4 space-y-3">
					{attributes.map((attribute) => {
						return (
							<PersonaliseField
								key={attribute.attribute.id}
								attribute={attribute.attribute}
								name={
									fieldPrefix
										? `${fieldPrefix}.${attribute.attribute.id}`
										: attribute.attribute.id || ""
								}
							/>
						);
					})}
				</div>
			</div>
		);
	}

	if (!checkout?.lines || !checkout.lines[0]) {
		return null;
	}
	const checkoutLine = checkout.lines[0];

	const renderAttributeValue = (attribute: AttributeFragment) => {
		// const value = get(initialValues, attribute.id);
		const value = initialValues[attribute.id];
		switch (attribute.inputType) {
			case AttributeInputTypeEnum.Dropdown: {
				// const selected = attribute.choices?.edges.find((i) => i.node.id === value);
				// return selected?.node.name;
				return value;
			}
			case AttributeInputTypeEnum.Boolean:
				return value ? t`Yes` : t`No`;
			case AttributeInputTypeEnum.RichText:
				return applyValueToCustomiseTemplate(value, user as User, checkout.profile);
			default:
				return value;
		}
	};

	if (!active) {
		return (
			<div className="grid grid-cols-4 gap-2">
				<div className="col-span-2 md:col-span-1 text-sm font-semibold pt-2">
					<Trans>Personalise</Trans>
				</div>
				<div className="col-span-4 md:col-span-3 border border-gray-300 rounded-md px-4 pt-3 pb-4 space-y-3">
					{attributes.map((attribute) => {
						return (
							<Stack
								key={attribute.attribute.id}
								direction="row"
								justifyContent="space-between"
								alignItems="center"
								spacing={2}
							>
								<div className="text-sm" style={{ maxWidth: "40%" }}>
									{attribute.attribute.name}
								</div>
								<div className="text-sm font-semibold">
									{renderAttributeValue(attribute.attribute)}
								</div>
							</Stack>
						);
					})}
				</div>
			</div>
		);
	}

	const handleSubmit = async (values: FormikValues, helpers: FormikHelpers<FormikValues>) => {
		const res = await Promise.all(
			attributes.map((attribute) => {
				const value =
					normalizePreferenceValue(
						attribute.attribute,
						values[attribute.attribute.id],
						checkout.profile,
						user as User
					) || "";
				if (savedPreferences[attribute.attribute.id]) {
					return updateCheckoutLinePreference({
						variables: {
							preferenceId: savedPreferences[attribute.attribute.id].id,
							value,
						},
					});
				}
				return createCheckoutLinePreference({
					variables: {
						attributeId: attribute.attribute.id,
						checkoutLineId: checkoutLine.id,
						value,
					},
				});
			})
		);
		setFieldValue("activeCustomizationId", undefined);
		return res;
	};

	// const handleApplyToAll = () => {};

	const handleCancel = () => {
		setFieldValue("activeCustomizationId", undefined);
	};

	const validateForm = (values: FormikValues) => {
		const errors: FormikErrors<FormikValues> = {};
		const validationMeta = getAttributesValidationMeta(attributes.map((c) => c.attribute));

		attributes.forEach(({ attribute }) => {
			const validationItem = validationMeta[attribute.id];
			if (validationItem) {
				if (validationItem.required && !values[attribute.id]) {
					errors[attribute.id] = t`Field is required`;
				} else if (validationItem.maxLength) {
					const templateValue = applyValueToCustomiseTemplate(
						values[attribute.id],
						user as User,
						checkout.profile
					);
					if (templateValue?.length > validationItem.maxLength) {
						errors[attribute.id] = plural(validationItem.maxLength, {
							one: "Field must be less than or equal to # character",
							other: "Field must be less than or equal to # characters",
						});
					}
				}
			}
		});
		return errors;
	};

	return (
		<Formik
			initialValues={initialValues}
			validate={validateForm}
			onSubmit={handleSubmit}
			validateOnMount={variant === "checkout"}
		>
			{({ submitForm, isSubmitting }) => (
				<div>
					<div className="grid grid-cols-4 gap-2">
						<div className="col-span-2 md:col-span-1 text-sm font-semibold pt-2">
							<Trans>Personalise</Trans>
						</div>
						<div className="col-span-4 md:col-span-3 border border-gray-300 rounded-md px-4 pt-3 pb-4 space-y-3">
							{attributes.map((attribute) => {
								return (
									<PersonaliseField
										key={attribute.attribute.id}
										attribute={attribute.attribute}
										name={attribute.attribute.id}
									/>
								);
							})}
						</div>
					</div>
					<Stack direction="row" spacing={2} mt={2} justifyContent="flex-end">
						{/* <Button variant='outlined' onClick={handleApplyToAll} disabled={isSubmitting}>
                        <Trans>Apply to all</Trans>
                    </Button> */}
						<Button variant="outlined" onClick={handleCancel} disabled={isSubmitting}>
							<Trans>Cancel</Trans>
						</Button>
						<Button
							variant="contained"
							disabled={isSubmitting}
							onClick={submitForm}
							endIcon={isSubmitting && <CircularProgress size={20} />}
						>
							<Trans>Save</Trans>
						</Button>
					</Stack>
				</div>
			)}
		</Formik>
	);
};

export default ProductPersonalize;
